import React, { Component } from 'react';
import axios from 'axios';
import Grid from '@material-ui/core/Grid';
import {
	Page,
	Text,
	View,
	Image,
	Font,
	Document,
	StyleSheet,
	PDFDownloadLink,
} from '@react-pdf/renderer';
import moment from 'moment';
import phoneFormatter from 'phone-formatter';
import styled from 'styled-components';
import getAmountLabel from '../../utils/getAmountLabel';
import dictionary from '../../utils/dictionary';

const acceptableLanguages = ['en', 'fr', 'es'];

const DownloadButton = styled.button`
	width: 100%;
	padding: 12px 24px;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	font-size: 14px;
	font-weight: 600;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	text-transform: none;
	background: #ffffff !important;
	color: #374151 !important;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

	&:hover {
		background: #f9fafb !important;
		border-color: #d1d5db;
		color: #1f2937 !important;
		transform: translateY(-1px);
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
	}

	&:active {
		background: #f3f4f6 !important;
		color: #1f2937 !important;
		transform: translateY(0);
	}

	&:focus {
		background: #ffffff !important;
		color: #374151 !important;
		outline: none;
		border-color: #3b82f6;
	}

	&:disabled {
		background: #f9fafb !important;
		color: #9ca3af !important;
		cursor: not-allowed;
		transform: none;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}

	@media (max-width: 480px) {
		padding: 10px 20px;
		font-size: 13px;
		border-radius: 10px;
	}
`;

const formatDate = ({ date }) =>
	moment.unix(date).format('MMM D, YYYY, h:mm A');

const formatPhone = ({ phone }) => {
	if (phone) {
		return phoneFormatter.format(phone, '(NNN) NNN NNNN');
	}
	return '';
};

/** Same rules as invoice page: initials when logo missing or failed to load. */
function getBusinessInitials(name, username) {
	const trimmedName = name && String(name).trim();
	if (trimmedName) {
		const initials = trimmedName
			.split(/\s+/)
			.map((word) => {
				const firstLetterOrDigit = word.match(/[\p{L}\p{N}]/u);
				return firstLetterOrDigit ? firstLetterOrDigit[0] : '';
			})
			.join('')
			.toUpperCase()
			.slice(0, 2);
		if (initials) return initials;
	}
	const u = username && String(username).trim();
	if (u) {
		const local = u.split('@')[0] || u;
		const two = local.slice(0, 2).toUpperCase();
		return two || '?';
	}
	return '?';
}

// PDF receipt — modern neutral layout
const styles = StyleSheet.create({
	page: {
		backgroundColor: '#ffffff',
		fontFamily: 'Open Sans',
		color: '#0f172a',
	},
	container: {
		flexDirection: 'row',
		paddingTop: 32,
		paddingHorizontal: 44,
		alignItems: 'flex-start',
	},
	section: {
		paddingHorizontal: 44,
		paddingTop: 28,
		paddingBottom: 12,
	},
	leftColumn: {
		flexDirection: 'column',
		width: '52%',
		paddingRight: 16,
	},
	rightColumn: {
		flex: 1,
		minWidth: 180,
	},
	receiptCard: {
		backgroundColor: '#f8fafc',
		borderRadius: 10,
		padding: 16,
		borderWidth: 1,
		borderColor: '#e2e8f0',
	},
	logoImage: {
		maxWidth: 100,
		maxHeight: 30,
		objectFit: 'contain',
		marginBottom: 10,
		alignSelf: 'flex-start',
	},
	logoInitials: {
		width: 30,
		height: 30,
		borderRadius: 6,
		borderWidth: 1,
		borderColor: '#e2e8f0',
		backgroundColor: '#f1f5f9',
		marginBottom: 10,
		justifyContent: 'center',
		alignItems: 'center',
		alignSelf: 'flex-start',
	},
	logoInitialsText: {
		fontSize: 11,
		fontFamily: 'Open Sans 600',
		color: '#475569',
	},
	businessTitle: {
		fontSize: 18,
		fontFamily: 'Open Sans 600',
		color: '#0f172a',
		marginBottom: 4,
		lineHeight: 1.25,
	},
	receiptTitle: {
		fontSize: 10,
		fontFamily: 'Open Sans 600',
		color: '#64748b',
		marginBottom: 12,
		textTransform: 'uppercase',
	},
	textBlock: {
		paddingTop: 4,
		paddingBottom: 14,
	},
	sectionLabel: {
		fontSize: 8,
		fontFamily: 'Open Sans 600',
		color: '#64748b',
		marginBottom: 8,
		textTransform: 'uppercase',
	},
	text: {
		fontSize: 10,
		paddingBottom: 4,
		fontFamily: 'Open Sans',
		color: '#334155',
		lineHeight: 1.45,
	},
	textMuted: {
		fontSize: 9,
		paddingBottom: 3,
		fontFamily: 'Open Sans',
		color: '#64748b',
		lineHeight: 1.4,
	},
	textBold: {
		fontSize: 10,
		paddingBottom: 2,
		fontFamily: 'Open Sans 600',
		color: '#0f172a',
	},
	metaLabel: {
		fontSize: 9,
		fontFamily: 'Open Sans 600',
		color: '#64748b',
	},
	table: {
		width: '100%',
		borderWidth: 1,
		borderColor: '#e2e8f0',
		borderRadius: 8,
	},
	tableHeaderRow: {
		flexDirection: 'row',
		backgroundColor: '#f1f5f9',
		borderBottomWidth: 1,
		borderBottomColor: '#e2e8f0',
	},
	tableRow: {
		flexDirection: 'row',
		borderBottomWidth: 1,
		borderBottomColor: '#f1f5f9',
		backgroundColor: '#ffffff',
	},
	tableRowRefund: {
		backgroundColor: '#fffbeb',
		borderBottomColor: '#fde68a',
	},
	tableCol: {
		width: '25%',
		paddingVertical: 10,
		paddingHorizontal: 12,
		justifyContent: 'flex-start',
	},
	tableHeaderCell: {
		fontSize: 7,
		fontFamily: 'Open Sans 600',
		color: '#64748b',
		textTransform: 'uppercase',
		letterSpacing: 0.6,
		lineHeight: 1.35,
	},
	tableCell: {
		fontSize: 9,
		fontFamily: 'Open Sans',
		color: '#0f172a',
		lineHeight: 1.35,
	},
});

Font.register({
	family: 'Open Sans',
	fonts: [
		{
			src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf',
		},
	],
});

Font.register({
	family: 'Open Sans 600',
	fonts: [
		{
			src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-600.ttf',
			fontWeight: 600,
		},
	],
});

class Receipt extends Component {
	state = {
		loading: false,
		error: false,
		receiptData: null,
		receiptLogoDataUrl: null,
	};

	// Stripe may omit or defer charges until a PaymentIntent succeeds; avoid crashes.
	// Newer API versions return an expanded `latest_charge` (or just its id, from
	// Stripe.js) instead of `charges.data`.
	getChargesData = (paymentIntent) => {
		if (!paymentIntent) {
			return [];
		}
		const latestCharge = paymentIntent.latest_charge;
		if (latestCharge && typeof latestCharge === 'object') {
			return [latestCharge];
		}
		if (
			!paymentIntent.charges ||
			!Array.isArray(paymentIntent.charges.data)
		) {
			return [];
		}
		return paymentIntent.charges.data;
	};

	getPrimaryChargeTimestamp = (paymentIntent) => {
		const charges = this.getChargesData(paymentIntent);
		if (charges.length > 0 && charges[0].created) {
			return charges[0].created;
		}
		return paymentIntent && paymentIntent.created
			? paymentIntent.created
			: null;
	};

	formatCardBrandLast4 = (paymentIntent) => {
		const pm = paymentIntent && paymentIntent.payment_method;
		if (!pm) {
			return '';
		}
		const card = pm.card;
		if (!card || !card.brand || !card.last4) {
			return '';
		}
		const brand = card.brand.replace(
			/^./,
			card.brand[0].toUpperCase()
		);
		return `${brand} ${card.last4}`;
	};

	getPaymentMethodTypeLabel = ({ paymentMethodDetails, fallbackPaymentIntent }) => {
		const { language } = this.state;
		const { creditCardText, directDebitText, usBankAchText } =
			dictionary[acceptableLanguages.includes(language) ? language : 'en'];

		const details =
			paymentMethodDetails ||
			(fallbackPaymentIntent &&
				fallbackPaymentIntent.payment_method_details) ||
			null;

		if (!details) {
			return creditCardText;
		}

		if (details.acss_debit) {
			return directDebitText;
		}

		if (details.us_bank_account) {
			return usBankAchText;
		}

		if (details.card) {
			return creditCardText;
		}

		return creditCardText;
	};

	formatPaymentMethodDetail = ({ paymentMethodDetails, fallbackPaymentIntent }) => {
		const details =
			paymentMethodDetails ||
			(fallbackPaymentIntent &&
				fallbackPaymentIntent.payment_method_details) ||
			null;

		if (!details) {
			return '-';
		}

		if (details.card && details.card.brand && details.card.last4) {
			return `${details.card.brand.replace(
				/^./,
				details.card.brand[0].toUpperCase()
			)} ${details.card.last4}`;
		}

		if (details.acss_debit) {
			const { bank_name: bankName, last4 } = details.acss_debit;
			if (bankName && last4) {
				return `${bankName} ****${last4}`;
			}
			if (last4) {
				return `****${last4}`;
			}
			if (bankName) {
				return bankName;
			}
			return 'Bank account';
		}

		if (details.us_bank_account) {
			const { bank_name: bankName, last4 } = details.us_bank_account;
			if (bankName && last4) {
				return `${bankName} ****${last4}`;
			}
			if (last4) {
				return `****${last4}`;
			}
			if (bankName) {
				return bankName;
			}
			return 'Bank account';
		}

		return '-';
	};

	formatPaymentMethodFromPaymentIntent = (paymentIntent) => {
		if (!paymentIntent || !paymentIntent.payment_method) {
			return '-';
		}

		const pm = paymentIntent.payment_method;
		if (pm.card && pm.card.brand && pm.card.last4) {
			return `${pm.card.brand.replace(/^./, pm.card.brand[0].toUpperCase())} ${
				pm.card.last4
			}`;
		}

		if (pm.acss_debit) {
			const { bank_name: bankName, last4 } = pm.acss_debit;
			if (bankName && last4) {
				return `${bankName} ****${last4}`;
			}
			if (last4) {
				return `****${last4}`;
			}
			if (bankName) {
				return bankName;
			}
			return 'Bank account';
		}

		if (pm.us_bank_account) {
			const { bank_name: bankName, last4 } = pm.us_bank_account;
			if (bankName && last4) {
				return `${bankName} ****${last4}`;
			}
			if (last4) {
				return `****${last4}`;
			}
			if (bankName) {
				return bankName;
			}
			return 'Bank account';
		}

		return '-';
	};

	async componentDidMount() {
		await this.setLanguage();
		this.setTitle();
		await this.getReceiptData();
	}

	setLanguage = async () => {
		const { language } = this.props;

		await this.setState({ language });
	};

	setTitle = () => {
		const { language } = this.state;
		const { title } =
			dictionary[acceptableLanguages.includes(language) ? language : 'en'];

		document.title = title;
	};

	getReceiptData = async () => {
		try {
			const { _invoice } = this.props;
			this.setState({ loading: true });

			const res = await axios.get(`/api/receipts/${_invoice}`);

			const { receiptData } = res.data;

			let receiptLogoDataUrl = null;
			try {
				const logoRes = await fetch(
					`/api/invoices/${_invoice}/logo`
				);
				if (logoRes.ok) {
					const blob = await logoRes.blob();
					receiptLogoDataUrl = await new Promise((resolve, reject) => {
						const fr = new FileReader();
						fr.onloadend = () => resolve(fr.result);
						fr.onerror = reject;
						fr.readAsDataURL(blob);
					});
				}
			} catch (logoErr) {
				// PDF will use initials fallback
			}

			this.setState({
				receiptData,
				receiptLogoDataUrl,
				loading: false,
			});
		} catch (err) {
			this.setState({ error: true, loading: false });
		}
	};

	renderAddress = (supportAddress) => {
		if (!supportAddress) {
			return ``;
		}

		if (supportAddress && supportAddress.postal_code) {
			return `${supportAddress.city}, ${supportAddress.state}, ${supportAddress.country}, ${supportAddress.postal_code}`;
		}

		return `${supportAddress.city}, ${supportAddress.state}, ${supportAddress.country}`;
	};

	// eslint-disable-next-line camelcase
	renderRefund = (refunds, payment_method_details) => {
		const { language } = this.state;

		// get labels from the dictionary for the appropriate language
		const { refundText } =
			dictionary[acceptableLanguages.includes(language) ? language : 'en'];

		const { currency } = this.props;

		const paymentMethodLabel = this.formatPaymentMethodDetail({
			paymentMethodDetails: payment_method_details,
		});

		return refunds.map((refund, index) => {
			return (
				<React.Fragment
					key={`refund-${index}-${refund.id || 'row'}`}
				>
				<View style={[styles.tableRow, styles.tableRowRefund]}>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>
							{formatDate({
								date: refund.created,
							})}
						</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>{refundText}</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>{paymentMethodLabel}</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>
							{getAmountLabel({
								amount: refund.amount,
								currency,
							})}{' '}
							{refund.currency.toUpperCase()}{' '}
						</Text>
					</View>
				</View>
				</React.Fragment>
			);
		});
	};

	renderPaymentLabel = ({ charge, hasConvenienceFee }) => {
		if (hasConvenienceFee) {
			return <Text style={styles.tableCell}>Total</Text>;
		}

		const detail = this.formatPaymentMethodDetail({
			paymentMethodDetails: charge.payment_method_details,
		});

		return <Text style={styles.tableCell}>{detail}</Text>;
	};

	renderTableRows = (
		charges,
		hasConvenienceFee,
		originalAmount,
		convenienceFee
	) => {
		const fallbackPaymentIntent =
			this.props &&
			this.props.invoiceData &&
			this.props.invoiceData.paymentIntent
				? this.props.invoiceData.paymentIntent
				: null;

		if (!charges || charges.length === 0) {
			return (
				<View style={styles.tableRow}>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>-</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>
							{this.getPaymentMethodTypeLabel({})}
						</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>-</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableCell}>-</Text>
					</View>
				</View>
			);
		}

		if (hasConvenienceFee) {
			return (
				<React.Fragment>
					<View style={styles.tableRow}>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{formatDate({
									date: charges[0].created,
								})}
							</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{this.getPaymentMethodTypeLabel({
									paymentMethodDetails: charges[0].payment_method_details,
								fallbackPaymentIntent,
								})}
							</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>Base Amount</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{getAmountLabel({
									amount: originalAmount,
									currency: this.props.currency,
								})}{' '}
								{charges.length > 0
									? charges[0].currency.toUpperCase()
									: ''}
							</Text>
						</View>
					</View>
					<View style={styles.tableRow}>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{formatDate({
									date: charges[0].created,
								})}
							</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{this.getPaymentMethodTypeLabel({
									paymentMethodDetails: charges[0].payment_method_details,
								fallbackPaymentIntent,
								})}
							</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>Convenience Fee</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{getAmountLabel({
									amount: convenienceFee,
									currency: this.props.currency,
								})}{' '}
								{charges.length > 0
									? charges[0].currency.toUpperCase()
									: ''}
							</Text>
						</View>
					</View>
					{charges.map((charge, index) => {
						return (
							<React.Fragment
								key={`charge-${index}-${charge.id || 'row'}`}
							>
								<View style={styles.tableRow}>
									<View style={styles.tableCol}>
										<Text style={styles.tableCell}>
											{formatDate({
												date: charge.created,
											})}
										</Text>
									</View>
									<View style={styles.tableCol}>
										<Text style={styles.tableCell}>
											{this.getPaymentMethodTypeLabel({
												paymentMethodDetails: charge.payment_method_details,
												fallbackPaymentIntent,
											})}
										</Text>
									</View>
									<View style={styles.tableCol}>
										{this.renderPaymentLabel({
											charge,
											hasConvenienceFee,
										})}
									</View>
									<View style={styles.tableCol}>
										<Text style={styles.tableCell}>
											{getAmountLabel({
												amount: charge.amount,
												currency: this.props.currency,
											})}{' '}
											{charge.currency.toUpperCase()}{' '}
										</Text>
									</View>
								</View>
								{this.renderRefund(
									(charge.refunds && charge.refunds.data) || [],
									charge.payment_method_details
								)}
							</React.Fragment>
						);
					})}
				</React.Fragment>
			);
		}

		return charges.map((charge, index) => {
			const methodLabel = this.formatPaymentMethodDetail({
				paymentMethodDetails: charge.payment_method_details,
				fallbackPaymentIntent,
			});
			const refundList =
				charge.refunds && Array.isArray(charge.refunds.data)
					? charge.refunds.data
					: [];

			return (
				<React.Fragment key={`charge-${index}-${charge.id || 'row'}`}>
					<View style={styles.tableRow}>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{formatDate({
									date: charge.created,
								})}
							</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{this.getPaymentMethodTypeLabel({
									paymentMethodDetails: charge.payment_method_details,
									fallbackPaymentIntent,
								})}
							</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>{methodLabel}</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableCell}>
								{getAmountLabel({
									amount: charge.amount,
									currency: this.props.currency,
								})}{' '}
								{charge.currency.toUpperCase()}{' '}
							</Text>
						</View>
					</View>
					{this.renderRefund(refundList, charge.payment_method_details)}
				</React.Fragment>
			);
		});
	};

	renderTable = (
		paymentIntent,
		hasConvenienceFee,
		originalAmount,
		convenienceFee
	) => {
		const { language } = this.state;

		// get labels from the dictionary for the appropriate language
		const { date, type, detail, amount } =
			dictionary[acceptableLanguages.includes(language) ? language : 'en'];

		const chargesData = this.getChargesData(paymentIntent);

		if (hasConvenienceFee) {
			return (
				<View style={styles.table}>
					<View style={styles.tableHeaderRow}>
						<View style={styles.tableCol}>
							<Text style={styles.tableHeaderCell}>{date}</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableHeaderCell}>{type}</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableHeaderCell}>{detail}</Text>
						</View>
						<View style={styles.tableCol}>
							<Text style={styles.tableHeaderCell}>{amount}</Text>
						</View>
					</View>
					{this.renderTableRows(
						chargesData,
						hasConvenienceFee,
						originalAmount,
						convenienceFee
					)}
				</View>
			);
		}

		return (
			<View style={styles.table}>
				<View style={styles.tableHeaderRow}>
					<View style={styles.tableCol}>
						<Text style={styles.tableHeaderCell}>{date}</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableHeaderCell}>{type}</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableHeaderCell}>{detail}</Text>
					</View>
					<View style={styles.tableCol}>
						<Text style={styles.tableHeaderCell}>{amount}</Text>
					</View>
				</View>
				{this.renderTableRows(chargesData)}
			</View>
		);
	};

	renderBillingDetails = (paymentIntent) => {
		const pm = paymentIntent && paymentIntent.payment_method;
		if (!pm || !pm.billing_details || !pm.billing_details.address) {
			return '';
		}
		const { city, state, country, postal_code: postalCode } =
			pm.billing_details.address;
		if (!city) {
			return '';
		}

		return `${city}, ${state}, ${country}, ${postalCode || ''}`;
	};

	renderLogoOrInitials = ({ receiptLogoDataUrl, name, username }) => {
		if (receiptLogoDataUrl) {
			return (
				<View>
					<Image src={receiptLogoDataUrl} style={styles.logoImage} />
				</View>
			);
		}
		return (
			<View style={styles.logoInitials}>
				<Text style={styles.logoInitialsText}>
					{getBusinessInitials(name, username)}
				</Text>
			</View>
		);
	};

	// Create Document Component
	renderReceipt = ({
		paymentIntent,
		customer,
		name: apiName,
		address: apiAddress,
		phone: apiPhone,
		username: apiUsername,
		receiptLogoDataUrl,
	}) => {
		const { language } = this.state;
		const inv = this.props.invoiceData;
		const name = (inv && inv.name) || apiName || '';
		const address = (inv && inv.address) || apiAddress || '';
		const phone = (inv && inv.phone) || apiPhone || '';
		const username = (inv && inv.username) || apiUsername || '';
		const { hasConvenienceFee, originalAmount, convenienceFee } = this.props;

		// get labels from the dictionary for the appropriate language
		const {
			paidBy,
			receipt,
			referenceNumber,
			description,
			datePaid,
			paymentMethod,
		} = dictionary[acceptableLanguages.includes(language) ? language : 'en'];

		const primaryPaidTs = this.getPrimaryChargeTimestamp(paymentIntent);
		const payerEmail =
			paymentIntent &&
			paymentIntent.payment_method &&
			paymentIntent.payment_method.billing_details &&
			paymentIntent.payment_method.billing_details.email
				? paymentIntent.payment_method.billing_details.email
				: '';
		const cardLine = this.formatPaymentMethodFromPaymentIntent(paymentIntent);
		const billingDetailsLine = this.renderBillingDetails(paymentIntent);

		if (hasConvenienceFee) {
			return (
				<Document>
					<Page size='A4' style={styles.page}>
						<View style={styles.container}>
							<View style={styles.leftColumn}>
								{this.renderLogoOrInitials({
									receiptLogoDataUrl,
									name,
									username,
								})}
								<Text style={styles.businessTitle}>{name}</Text>
								<View style={styles.textBlock}>
									<Text style={styles.text}>{address}</Text>
									<Text style={styles.text}>
										{formatPhone({
											phone,
										})}
									</Text>
									{username ? (
										<Text style={styles.textMuted}>{username}</Text>
									) : null}
								</View>
								<View style={styles.textBlock}>
									<Text style={styles.sectionLabel}>{paidBy}</Text>
									<Text style={styles.text}>
										{customer && customer.name ? customer.name : ''}
									</Text>
									{billingDetailsLine ? (
										<Text style={styles.textMuted}>{billingDetailsLine}</Text>
									) : null}
									<Text style={styles.text}>
										{formatPhone({
											phone: customer && customer.phone,
										})}
									</Text>
									{payerEmail ? (
										<Text style={styles.textMuted}>{payerEmail}</Text>
									) : null}
								</View>
							</View>
							<View style={styles.rightColumn}>
								<View style={styles.receiptCard}>
									<Text style={styles.receiptTitle}>{receipt}</Text>
									<Text style={styles.text}>
										<Text style={styles.metaLabel}>
											{referenceNumber}:{' '}
										</Text>
										{paymentIntent.metadata &&
											paymentIntent.metadata.reference_number}
									</Text>
									<Text style={styles.text}>
										<Text style={styles.metaLabel}>{description}: </Text>
										{paymentIntent.description}
									</Text>
									<Text style={styles.text}>
										<Text style={styles.metaLabel}>{datePaid}: </Text>
										{primaryPaidTs
											? formatDate({
													date: primaryPaidTs,
											  })
											: '-'}
									</Text>
									<Text style={styles.text}>
										<Text style={styles.metaLabel}>{paymentMethod}: </Text>
										{cardLine}
									</Text>
								</View>
							</View>
						</View>

						<View style={styles.section}>
							{this.renderTable(
								paymentIntent,
								hasConvenienceFee,
								originalAmount,
								convenienceFee
							)}
						</View>
					</Page>
				</Document>
			);
		}

		return (
			<Document>
				<Page size='A4' style={styles.page}>
					<View style={styles.container}>
						<View style={styles.leftColumn}>
							{this.renderLogoOrInitials({
								receiptLogoDataUrl,
								name,
								username,
							})}
							<Text style={styles.businessTitle}>{name}</Text>
							<View style={styles.textBlock}>
								<Text style={styles.text}>{address}</Text>
								<Text style={styles.text}>
									{formatPhone({
										phone,
									})}
								</Text>
								{username ? (
									<Text style={styles.textMuted}>{username}</Text>
								) : null}
							</View>
							<View style={styles.textBlock}>
								<Text style={styles.sectionLabel}>{paidBy}</Text>
								<Text style={styles.text}>
									{customer && customer.name ? customer.name : ''}
								</Text>
								{billingDetailsLine ? (
									<Text style={styles.textMuted}>{billingDetailsLine}</Text>
								) : null}
								<Text style={styles.text}>
									{formatPhone({
										phone: customer && customer.phone,
									})}
								</Text>
								{payerEmail ? (
									<Text style={styles.textMuted}>{payerEmail}</Text>
								) : null}
							</View>
						</View>
						<View style={styles.rightColumn}>
							<View style={styles.receiptCard}>
								<Text style={styles.receiptTitle}>{receipt}</Text>
								<Text style={styles.text}>
									<Text style={styles.metaLabel}>
										{referenceNumber}:{' '}
									</Text>
									{paymentIntent.metadata &&
										paymentIntent.metadata.reference_number}
								</Text>
								<Text style={styles.text}>
									<Text style={styles.metaLabel}>{description}: </Text>
									{paymentIntent.description}
								</Text>
								<Text style={styles.text}>
									<Text style={styles.metaLabel}>{datePaid}: </Text>
									{primaryPaidTs
										? formatDate({
												date: primaryPaidTs,
										  })
										: '-'}
								</Text>
								<Text style={styles.text}>
									<Text style={styles.metaLabel}>{paymentMethod}: </Text>
									{cardLine}
								</Text>
							</View>
						</View>
					</View>

					<View style={styles.section}>{this.renderTable(paymentIntent)}</View>
				</Page>
			</Document>
		);
	};

	renderLoading = () => {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: 52,
					padding: '8px 0 4px',
				}}
			>
				<DownloadButton disabled>
					<svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
						<path
							d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
						<polyline
							points='7,10 12,15 17,10'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
						<line
							x1='12'
							y1='15'
							x2='12'
							y2='3'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
					</svg>
					Loading...
				</DownloadButton>
			</div>
		);
	};

	renderError = () => {
		const { language } = this.state;
		const { errorTitle, errorMessage } =
			dictionary[acceptableLanguages.includes(language) ? language : 'en'];

		return (
			<Grid
				container
				direction='column'
				justify='center'
				alignItems='center'
				style={{ minHeight: 80, padding: '16px 0' }}
			>
				<Grid item>
					<h3>{errorTitle}</h3>
				</Grid>

				<Grid item>
					<div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
						{errorMessage}
					</div>
				</Grid>
			</Grid>
		);
	};

	render() {
		const { receiptData, loading, error, language } = this.state;

		const { downloadReceipt, receipt } =
			dictionary[acceptableLanguages.includes(language) ? language : 'en'];
		// set the language of moment
		moment.locale(language || 'en');

		if (loading) {
			return this.renderLoading();
		}

		if (error) {
			return this.renderError();
		}

		if (receiptData) {
			const { receiptLogoDataUrl } = this.state;

			return (
				<div>
					<PDFDownloadLink
						document={this.renderReceipt({
							...receiptData,
							receiptLogoDataUrl,
						})}
						fileName={`${receipt} ${moment().format('MMM DD YYYY')}`}
					>
						{({ loading: pdfLoading }) => (
							<DownloadButton disabled={pdfLoading}>
								<svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
									<path
										d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'
										stroke='currentColor'
										strokeWidth='2'
										strokeLinecap='round'
										strokeLinejoin='round'
									/>
									<polyline
										points='7,10 12,15 17,10'
										stroke='currentColor'
										strokeWidth='2'
										strokeLinecap='round'
										strokeLinejoin='round'
									/>
									<line
										x1='12'
										y1='15'
										x2='12'
										y2='3'
										stroke='currentColor'
										strokeWidth='2'
										strokeLinecap='round'
										strokeLinejoin='round'
									/>
								</svg>
								{pdfLoading ? 'Generating...' : downloadReceipt}
							</DownloadButton>
						)}
					</PDFDownloadLink>
				</div>
			);
		}

		return <span />;
	}
}

export default Receipt;
